import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, MessageCircle, Crown, ShieldCheck } from "lucide-react";
import { useAuth } from "@/App";
import axios from "axios";
import BottomNav from "@/components/BottomNav";
import { formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${API}/conversations`);
        setConversations(response.data.conversations || []);
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24" data-testid="messages-page">
      {/* Decorative blobs */}
      <div className="blob blob-pink w-64 h-64 -top-32 -right-32 fixed opacity-30" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-100">
        <div className="max-w-lg mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Messages
            </h1>
            <div className="w-6" />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No messages yet
            </h2>
            <p className="text-slate-500 mb-6">
              Match with families to start chatting!
            </p>
            <Link to="/discover">
              <button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6 py-3 font-medium" data-testid="find-families-btn">
                Find Families
              </button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <Link key={conv.conversation_id} to={`/messages/${conv.conversation_id}`}>
                <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all flex items-center gap-4" data-testid={`conversation-${conv.conversation_id}`}>
                  {/* Avatar */}
                  <div className={`w-14 h-14 rounded-full flex-shrink-0 ring-4 ring-teal-100 overflow-hidden ${
                    conv.other_user?.is_blurred ? 'bg-gradient-to-br from-sky-400 to-purple-400' : ''
                  }`}>
                    {!conv.other_user?.is_blurred && (conv.other_user?.photo_url || conv.other_user?.user_picture) ? (
                      <img 
                        src={conv.other_user.photo_url || conv.other_user.user_picture} 
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                        {conv.other_user?.name?.[0] || '?'}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800 truncate">{conv.other_user?.name}</h3>
                      {conv.other_user?.is_verified && (
                        <ShieldCheck className="w-4 h-4 text-teal-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {conv.last_message?.content || "Start a conversation!"}
                    </p>
                  </div>

                  {/* Time */}
                  {conv.last_message_at && (
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: false })}
                    </span>
                  )}
                </div>
              </Link>
            ))}

            {/* Photo blur notice for free users */}
            {!user?.is_premium && (
              <Link to="/subscription">
                <div className="mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-200 flex items-center gap-3" data-testid="premium-notice">
                  <Crown className="w-6 h-6 text-amber-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">See profile photos</p>
                    <p className="text-xs text-amber-600">Upgrade to premium</p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Messages;